import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Principal, Permission, LoginResult } from 'shared';
import { api, tokenStore, ApiError } from '../lib/api';

interface AuthState {
  user: Principal | null;
  permissions: Permission[];
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  can: (p: Permission) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Principal | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * The guard is a ref rather than a boolean flag inside the effect. React StrictMode
   * invokes effects twice in development; with a plain flag the screen can hang on
   * loading forever (gotcha #11 in the microsite document).
   */
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    if (!tokenStore.get()) {
      setLoading(false);
      return;
    }

    api.get<{ user: Principal; permissions: Permission[] }>('/auth/me')
      .then((d) => { setUser(d.user); setPermissions(d.permissions); })
      .catch(() => { tokenStore.clear(); })
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const res = await api.post<LoginResult>('/auth/login', { email, password });
    tokenStore.set(res.token);
    const me = await api.get<{ user: Principal; permissions: Permission[] }>('/auth/me');
    setUser(me.user);
    setPermissions(me.permissions);
  }

  function logout() {
    tokenStore.clear();
    setUser(null);
    setPermissions([]);
  }

  return (
    <AuthContext.Provider
      value={{ user, permissions, loading, login, logout, can: (p) => permissions.includes(p) }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth harus dipakai di dalam AuthProvider');
  return ctx;
}

export { ApiError };
